import { boot } from '@dazn/bootstrap-lib';
import { FeaturevisorInstance } from '@featurevisor/sdk';
import { EventName } from '@featurevisor/sdk/lib/emitter';
import { StickyFeatures } from '@featurevisor/types';

import {
    environments,
    KNOWN_SESSION,
    LAST_KNOWN_USER_STATUS,
} from '../configuration/constants';
import getCookie from '../cookieBanner/getCookie';

import { setFeaturevisorLogLevels } from '../featurevisor/logLevel';

import { getLivingRoomAttributes } from './getLivingRoomAttributes';
import { getLocaleFromURL } from './getLocaleFromURL';
import { maybeWrongMethodWarn } from './maybeWrongMethodWarn';
import { warnAboutDuplictaes } from './warnAboutDuplictaes';

type CustomAttributes = boot.fflag.CustomAttributes;

interface Listener {
    id: string;
    callback: (params?: any) => void;
    eventName: EventName;
}

export interface CreateFFlagModuleProps {
    currentEnvironment: boot.config.Environment;
    environmentGroup: string;
    getViewerId: () => string;
    getDeviceId: () => string;
    getAccountStatus: () => string;
    getBrowser: () => string;
    getBrowserVersion: () => string;
    getUserCountryCode: () => string;
    getBillingCountry: () => string | undefined;
    getGeolocatedCountry: () => string;
    getGeolocatedZipcode: () => string;
    getDeviceType: () => string;
    getUserLanguage: () => string;
    getOs: () => string;
    getChapterName: () => string;
    getFullURL: () => string;
    /**
     * @example 'magentatv' || 'ps5'
     */
    getPlatform: () => string;
    getUserConsent: () => Promise<boot.user.UserConsent>;
    /**
     * DO NOT USE. For PPV testing only, to be removed.
     */
    getIsDevModeEnabled: () => boolean;
    getUserNflStatus: () => string;
    getUserNhlStatus: () => string;
    getUserBillingRouting: () => string;
    getAnyOfTheProductGroupIsActivePaid: () => boolean;
    getUserState: () => string;
    asyncStorage: boot.storage.AsyncStorage;
    getNativeAppName: () => string;
    getBrandName: () => string;
    getUserType: () => number;
}

/**
 * Bootstrap feature flagging module
 * Provides a high level API abstraction
 * Uses Optimizely of Featurevisor under the hood
 */
export const createFeatureFlagModule = ({
    currentEnvironment,
    environmentGroup,
    getViewerId,
    getDeviceId,
    getAccountStatus,
    getBrowser,
    getBrowserVersion,
    getUserCountryCode,
    getBillingCountry,
    getGeolocatedCountry,
    getGeolocatedZipcode,
    getDeviceType,
    getUserLanguage,
    getOs,
    getChapterName,
    getFullURL,
    getPlatform,
    getUserConsent,
    getIsDevModeEnabled,
    getUserNflStatus,
    getUserNhlStatus,
    getUserBillingRouting,
    getAnyOfTheProductGroupIsActivePaid,
    getUserState,
    asyncStorage,
    getNativeAppName,
    getBrandName,
    getUserType,
}: CreateFFlagModuleProps) => {
    let featurevisorClientInstance: FeaturevisorInstance | null = null;
    const stickyFeatures: StickyFeatures = {};
    let listeners: Listener[] = [];

    // #region Attributes

    let deviceMetadata: boot.device.DeviceMetadata = {};
    let manufacturer = 'N/A';
    const isProd = currentEnvironment === environments.PRODUCTION;

    let trackOptimizelyFallback = false;

    let userConsentAttributes: boot.fflag.ConsentAttributes;
    const refreshUserConsentAttributes = () =>
        getUserConsent().then(consent => {
            userConsentAttributes = {
                PerformanceConsent: consent.performance,
                FunctionalConsent: consent.functional,
                TargetingConsent: consent.targeting,
                GoogleAdsStorageConsent: consent.googleAdsStorage,
                PersonalisedAdsConsent: consent.personalisedAds,
            };
        });

    const getDefaultAttributes = (): boot.fflag.DefaultAttributes => {
        const fullURL = getFullURL();
        const { language = '', country = '' } = getLocaleFromURL(fullURL);

        // these will refresh async - we can't change all the function signatures
        // so the first activation after a cookie change will probably be outdated
        // we just have to deal with that for now - it's caused by the StorageAPI being async
        refreshUserConsentAttributes();

        const isVipUser = window.dazn?.auth?.isVip();

        const genericAttributes: boot.fflag.GenericAttributes = {
            AccountStatus: getAccountStatus(),
            Browser: getBrowser(),
            BrowserVersion: getBrowserVersion(),
            /**
             * @deprecated use BillingCountry & GeolocatedCountry instead
             */
            UserCountryCode: getUserCountryCode()?.toLowerCase(),
            Type: getDeviceType(),
            UserLanguageLocaleKey: getUserLanguage(),
            Os: getOs(),
            Chapter: getChapterName(),
            OptimizelyEnvironment: currentEnvironment,
            FullURL: fullURL,
            URLCountry: country.toLowerCase(),
            URLLanguage: language,
            Platform: getPlatform(),
            BillingCountry: getBillingCountry()?.toLowerCase(),
            GeolocatedCountry: getGeolocatedCountry()?.toLowerCase(),
            GeolocatedZip: getGeolocatedZipcode(),
            DevMode: getIsDevModeEnabled() && !isVipUser,
            ReferralURL: document?.referrer ?? '',
            EnvironmentGroup: environmentGroup,
            ProductStatusNFL: getUserNflStatus(),
            ProductStatusNHL: getUserNhlStatus(),
            UserBillingRouting: getUserBillingRouting(),
            AnyOfTheProductGroupIsActivePaid:
                getAnyOfTheProductGroupIsActivePaid(),
            NativeAppName: getNativeAppName(),
            IsVipUser: isVipUser,
            UserState: getUserState(),
            Brand: getBrandName(),
            KnownSession: isKnownSession(),
            userType: getUserType(),
            lastKnownUserStatus: getLastKnownUserStatus(),
        };

        return {
            ...genericAttributes,
            ...getLivingRoomAttributes(deviceMetadata, manufacturer),
            ...userConsentAttributes,
        };
    };

    const isKnownSession = (): boolean => {
        const cookieValue = getCookie(KNOWN_SESSION);
        return (
            (cookieValue && (cookieValue === true || cookieValue === 'true')) ??
            false
        );
    };

    const getLastKnownUserStatus = (): string => {
        const cookieValue = getCookie(LAST_KNOWN_USER_STATUS);
        return cookieValue ?? 'unknown';
    };

    const combineFinalAttrs = (
        trackerSpecificAttrs: CustomAttributes,
        customAttrs: CustomAttributes | undefined,
    ) => {
        const defaultAttrs = getDefaultAttributes();
        if (customAttrs) {
            warnAboutDuplictaes(customAttrs, defaultAttrs);
        }
        return {
            ...customAttrs,
            ...defaultAttrs,
            ...trackerSpecificAttrs,
        };
    };

    // #endregion

    // #region Methods

    type FeatAttrs = {
        optimizelyId: string;
        optimizelyAttrs: CustomAttributes | null;
        featurevisorAttrs: CustomAttributes;
    };

    type GetUserFeatData =
        | typeof getAnonymousUserData
        | typeof getAuthenticatedUserData;

    /**
     * Returns tracker custom ID for anonymous users
     */
    const getAnonymousUserData = () => {
        const deviceId = getDeviceId();
        const id = getViewerId();
        const featurevisorAttrs = {
            deviceId,
            ...(id && { userId: id }),
        };
        return {
            optimizelyId: deviceId,
            optimizelyAttrs: {},
            featurevisorAttrs,
        } satisfies FeatAttrs;
    };
    /**
     * Returns tracker custom ID for anonymous users
     */
    const getAuthenticatedUserData = () => {
        const id = getViewerId();
        const deviceId = getDeviceId();
        maybeWrongMethodWarn(id);
        return {
            optimizelyId: id,
            optimizelyAttrs: {
                ViewerID: id,
            },
            featurevisorAttrs: {
                userId: id,
                deviceId,
            },
        } satisfies FeatAttrs;
    };

    const activateIfNeeded = (
        feature: boot.featurevisor.Feature,
        context: Record<string, any>,
    ) => {
        if (!featurevisorClientInstance || feature.key.startsWith('e_')) return;
        if (feature.variations) {
            featurevisorClientInstance.activate(feature.key, context);
        }
    };

    const callFeature = <T>(feat: {
        featName: string;
        customAttrs: CustomAttributes | undefined;
        getUserData: GetUserFeatData;
        callFeaturevisor: (fv: {
            client: FeaturevisorInstance;
            attrs: boot.fflag.Attributes;
        }) => T;
        caller: FeatureFlagCaller;
    }): NonNullable<T> | null => {
        if (featurevisorClientInstance) {
            const hasFeature = featurevisorClientInstance.getFeature(
                feat.featName,
            );
            if (hasFeature) {
                const combinedAttrs = combineFinalAttrs(
                    feat.getUserData().featurevisorAttrs,
                    feat.customAttrs,
                ) as boot.fflag.Attributes & { __caller: FeatureFlagCaller };

                combinedAttrs.__caller = feat.caller;

                // TODO: Create a local var to track the activated feature keys
                activateIfNeeded(hasFeature, combinedAttrs);

                return (
                    feat.callFeaturevisor({
                        client: featurevisorClientInstance,
                        attrs: combinedAttrs,
                    }) ?? null
                );
            }
        }

        if (trackOptimizelyFallback) {
            window.newrelic?.addPageAction('OptimizelyFallback', {
                featureKey: feat.featName,
                chapter: getChapterName(),
                platform: getPlatform(),
            });
        }

        return null;
    };

    const createGetVariation =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (experimentName: string, customAttrs?: CustomAttributes) =>
            callFeature({
                featName: experimentName,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.getVariation(experimentName, fv.attrs),
            });

    const createIsFeatureEnabled =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (experimentName: string, customAttrs?: CustomAttributes) =>
            callFeature({
                featName: experimentName,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.isEnabled(experimentName, fv.attrs),
            });

    // TODO: FV analog?
    const createTrack =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (
            experimentName: string,
            customAttrs?: CustomAttributes,
            eventTags?: boot.fflag.EventTags,
        ) => {
            console.warn('DEPRECATED! Use fe-events for tracking purpose');
        };

    const createActivate =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (experimentName: string, customAttrs?: CustomAttributes) =>
            callFeature({
                featName: experimentName,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.activate(experimentName, fv.attrs),
            });

    const createGetVariableBoolean =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (
            experimentKey: string,
            variableKey: string,
            customAttrs?: CustomAttributes,
        ) =>
            callFeature({
                featName: experimentKey,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.getVariableBoolean(
                        experimentKey,
                        variableKey,
                        fv.attrs,
                    ),
            });

    const createGetVariableInteger =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (
            experimentKey: string,
            variableKey: string,
            customAttrs?: CustomAttributes,
        ) =>
            callFeature({
                featName: experimentKey,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.getVariableInteger(
                        experimentKey,
                        variableKey,
                        fv.attrs,
                    ),
            });

    const createGetVariableString =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (
            experimentKey: string,
            variableKey: string,
            customAttrs?: CustomAttributes,
        ) =>
            callFeature({
                featName: experimentKey,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.getVariableString(
                        experimentKey,
                        variableKey,
                        fv.attrs,
                    ),
            });

    const createGetVariableObject =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (
            experimentKey: string,
            variableKey: string,
            customAttrs?: CustomAttributes,
        ) =>
            callFeature({
                featName: experimentKey,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.getVariableObject(
                        experimentKey,
                        variableKey,
                        fv.attrs,
                    ),
            });

    // #endregion
    const createGetVariableArray =
        (getUserData: GetUserFeatData, caller: FeatureFlagCaller) =>
        (
            experimentKey: string,
            variableKey: string,
            customAttrs?: CustomAttributes,
        ) =>
            callFeature({
                featName: experimentKey,
                customAttrs,
                getUserData,
                caller,
                callFeaturevisor: fv =>
                    fv.client.getVariableArray(
                        experimentKey,
                        variableKey,
                        fv.attrs,
                    ),
            });
    const trackFeaturevisorErrors = () => {
        if (!featurevisorClientInstance) return;

        return featurevisorClientInstance.getVariable(
            'f_migration',
            'trackFeaturevisorErrors',
            combineFinalAttrs(getAnonymousUserData().featurevisorAttrs, {}),
        );
    };

    return {
        // #region API

        getVariationAnonymous: createGetVariation(
            getAnonymousUserData,
            'getVariationAnonymous',
        ),
        getVariationAuthenticated: createGetVariation(
            getAuthenticatedUserData,
            'getVariationAuthenticated',
        ),

        activateAnonymous: createActivate(
            getAnonymousUserData,
            'activateAnonymous',
        ),
        activateAuthenticated: createActivate(
            getAuthenticatedUserData,
            'activateAuthenticated',
        ),

        trackAnonymous: createTrack(getAnonymousUserData, 'trackAnonymous'),
        trackAuthenticated: createTrack(
            getAuthenticatedUserData,
            'trackAuthenticated',
        ),

        isEnabledAnonymous: createIsFeatureEnabled(
            getAnonymousUserData,
            'isEnabledAnonymous',
        ),
        isEnabledAuthenticated: createIsFeatureEnabled(
            getAuthenticatedUserData,
            'isEnabledAuthenticated',
        ),

        getVariableBooleanAnonymous: createGetVariableBoolean(
            getAnonymousUserData,
            'getVariableBooleanAnonymous',
        ),
        getVariableBooleanAuthenticated: createGetVariableBoolean(
            getAuthenticatedUserData,
            'getVariableBooleanAuthenticated',
        ),
        getVariableIntegerAnonymous: createGetVariableInteger(
            getAnonymousUserData,
            'getVariableIntegerAnonymous',
        ),
        getVariableIntegerAuthenticated: createGetVariableInteger(
            getAuthenticatedUserData,
            'getVariableIntegerAuthenticated',
        ),

        getVariableStringAnonymous: createGetVariableString(
            getAnonymousUserData,
            'getVariableStringAnonymous',
        ),
        getVariableStringAuthenticated: createGetVariableString(
            getAuthenticatedUserData,
            'getVariableStringAuthenticated',
        ),

        getVariableObjectAnonymous: createGetVariableObject(
            getAnonymousUserData,
            'getVariableObjectAnonymous',
        ),
        getVariableObjectAuthenticated: createGetVariableObject(
            getAuthenticatedUserData,
            'getVariableObjectAuthenticated',
        ),
        getVariableArrayAnonymous: createGetVariableArray(
            getAnonymousUserData,
            'getVariableArrayAnonymous',
        ),
        getVariableArrayAuthenticated: createGetVariableArray(
            getAuthenticatedUserData,
            'getVariableArrayAuthenticated',
        ),

        trackFeaturevisorErrors,
        //
        // Forced variations
        //
        setForcedVariationAnonymous(
            experimentKey: string,
            forcedVariation: string | null,
        ) {
            if (!featurevisorClientInstance) return false;
            if (typeof stickyFeatures[experimentKey] === 'undefined') {
                stickyFeatures[experimentKey] = {
                    enabled: true,
                    variation: forcedVariation as string,
                };
            } else {
                stickyFeatures[experimentKey].enabled = true;
                stickyFeatures[experimentKey].variation =
                    forcedVariation as string;
            }

            featurevisorClientInstance.setStickyFeatures(stickyFeatures);
            return true;
        },
        getForcedVariationAnonymous(experimentKey: string) {
            if (!featurevisorClientInstance) return;
            return featurevisorClientInstance.getVariation(experimentKey, {});
        },
        setForcedVariationAuthenticated(
            experimentKey: string,
            forcedVariation: string | null,
        ) {
            if (!featurevisorClientInstance) return false;
            if (typeof stickyFeatures[experimentKey] === 'undefined') {
                stickyFeatures[experimentKey] = {
                    enabled: true,
                    variation: forcedVariation as string,
                };
            } else {
                stickyFeatures[experimentKey].enabled = true;
                stickyFeatures[experimentKey].variation =
                    forcedVariation as string;
            }

            featurevisorClientInstance.setStickyFeatures(stickyFeatures);
            return true;
        },
        getForcedVariationAuthenticated(experimentKey: string) {
            if (!featurevisorClientInstance) return;
            return featurevisorClientInstance.getVariation(experimentKey, {});
        },
        //
        // Notifications
        //
        addNotificationListener(
            notificationType: string,
            callback: (params?: any) => void,
        ) {
            if (!featurevisorClientInstance) return 0;

            const id = getDeviceId();
            listeners.push({
                id,
                callback,
                eventName: notificationType as EventName,
            });

            featurevisorClientInstance.on(
                notificationType as EventName,
                callback,
            );
            return id;
        },
        removeNotificationListener(listenerId: number | string) {
            const existingListener = listeners.find(
                listener => listener.id === listenerId,
            );

            if (!existingListener) return false;

            const { eventName, callback } = existingListener;

            featurevisorClientInstance?.off(eventName, callback);

            listeners = listeners.filter(
                listener => listener.id !== listenerId,
            );

            return true;
        },
        // #endregion

        run: (
            fInstance: FeaturevisorInstance | null,
            inputDeviceMetadata: boot.device.DeviceMetadata,
            inputManufacturer: string,
        ) => {
            featurevisorClientInstance = fInstance;

            if (featurevisorClientInstance) {
                const attrs = combineFinalAttrs(
                    getAnonymousUserData().featurevisorAttrs,
                    {},
                );
                trackOptimizelyFallback =
                    featurevisorClientInstance.getVariableBoolean(
                        'f_migration',
                        'trackOptimizelyFallback',
                        attrs,
                    ) ?? false;
            }

            return refreshUserConsentAttributes().then(() => {
                deviceMetadata = inputDeviceMetadata;
                manufacturer = inputManufacturer;
            });
        },

        setOptimizelyLogLevel: (
            level: boot.optimizely.ConsumerDefinedLogLevel | null = null,
        ) => {
            console.log('SET OPTIMIZELY');
            return Promise.resolve();
        },

        setFeaturevisorLogLevelsUpdated: (
            levels: boot.optimizely.ConsumerDefinedLogLevel[],
        ) =>
            setFeaturevisorLogLevels({
                asyncStorage,
                levels,
                isDevMode: getIsDevModeEnabled(),
                isProd,
            }).then(logLevel => {
                if (logLevel && logLevel.length > 0)
                    featurevisorClientInstance?.setLogLevels(logLevel);
            }),

        combineFinalAttrs,
        getDefaultAttributes,
    };
};

export type FeatureFlagCaller =
    | 'getVariationAnonymous'
    | 'getVariationAuthenticated'
    | 'activateAnonymous'
    | 'activateAuthenticated'
    | 'trackAnonymous'
    | 'trackAuthenticated'
    | 'isEnabledAnonymous'
    | 'isEnabledAuthenticated'
    | 'getVariableBooleanAnonymous'
    | 'getVariableBooleanAuthenticated'
    | 'getVariableIntegerAnonymous'
    | 'getVariableIntegerAuthenticated'
    | 'getVariableStringAnonymous'
    | 'getVariableStringAuthenticated'
    | 'getVariableObjectAnonymous'
    | 'getVariableObjectAuthenticated'
    | 'getVariableArrayAnonymous'
    | 'getVariableArrayAuthenticated';

export type FeaturesModule = ReturnType<typeof createFeatureFlagModule>;
